package com.arena_web.ui.components.events

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

@Composable
fun DeleteEventConfirmDialog(
    isOpen: Boolean,
    eventTitle: String,
    isDeleting: Boolean,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
    participantCount: Int = 1,
) {
    if (!isOpen) return

    // Backend behaviour (DeleteEventUseCase): 2+ participants -> soft cancel
    // (status=CANCELLED, chat & image preserved); 1 -> full delete (event row,
    // chat, R2 image). Mirror that in the dialog copy so admins know what
    // they're triggering.
    val willCancel = participantCount >= 2
    val t = LocalAppStrings.current

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { if (!isDeleting) onCancel() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .sMaxWidth(420)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(26)
                .sPadding(28, 26)
                .sGap(20)
                .onClick { it.stopPropagation() }
                .animation(
                    PopupSlideUp.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("background-color", "var(--l-popup, var(--l-s2, #09142D))")
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                    property("border", "0.5px solid var(--l-hairline, rgba(255, 255, 255, 0.06))")
                }
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().sGap(14),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                val iconBg = if (willCancel) "rgba(255, 165, 0, 0.14)" else "rgba(255, 26, 26, 0.14)"
                val iconBorder = if (willCancel) "1px solid rgba(255, 165, 0, 0.35)" else "1px solid rgba(255, 26, 26, 0.35)"
                val iconFilter = if (willCancel) {
                    "brightness(0) saturate(100%) invert(68%) sepia(82%) saturate(2000%) hue-rotate(360deg) brightness(101%) contrast(101%)"
                } else {
                    "brightness(0) saturate(100%) invert(33%) sepia(99%) saturate(7400%) hue-rotate(354deg) brightness(102%) contrast(108%)"
                }
                Box(
                    modifier = Modifier
                        .sSize(44)
                        .sBorderRadius(100)
                        .styleModifier {
                            property("background-color", iconBg)
                            property("border", iconBorder)
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        src = if (willCancel) AppIcons.Close else AppIcons.Trash,
                        modifier = Modifier.sSize(20).styleModifier {
                            property("filter", iconFilter)
                        }
                    )
                }
                SpanText(
                    if (willCancel) t.eventCancelEventQuestion else t.eventDeleteEventQuestion,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(FontWeight.SemiBold)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier {
                            property("font-family", "Urbanist, sans-serif")
                            property("text-align", "center")
                        }
                )
            }

            Column(
                modifier = Modifier.fillMaxWidth().sGap(6),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    if (willCancel) {
                        t.eventWillBeCancelled(eventTitle)
                    } else {
                        t.eventWillBeDeleted(eventTitle)
                    },
                    modifier = Modifier
                        .sFontSize(15)
                        .color(Color("var(--arena-text-2)"))
                        .styleModifier {
                            property("font-family", "Urbanist, sans-serif")
                            property("line-height", "1.5")
                            property("text-align", "center")
                        }
                )
                SpanText(
                    if (willCancel) {
                        t.eventCancelNoticeParticipants
                    } else {
                        t.eventDeleteNoticeMedia
                    },
                    modifier = Modifier
                        .sFontSize(13)
                        .color(Color("var(--arena-text-muted)"))
                        .styleModifier {
                            property("font-family", "Urbanist, sans-serif")
                            property("line-height", "1.5")
                            property("text-align", "center")
                        }
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                DialogPillButton(
                    text = if (willCancel) t.eventKeepEvent else t.commonCancel,
                    onClick = { if (!isDeleting) onCancel() },
                    background = AppGradients.InteractiveSecondary,
                    textColor = "#BED6F5",
                    enabled = !isDeleting,
                )
                val confirmText = when {
                    isDeleting && willCancel -> t.eventCancelling
                    isDeleting -> t.eventDeleting
                    willCancel -> t.eventCancelEvent
                    else -> t.commonDelete
                }
                DialogPillButton(
                    text = confirmText,
                    onClick = { if (!isDeleting) onConfirm() },
                    background = if (willCancel) "#FFA500" else "#FF1A1A",
                    textColor = "#FFFFFF",
                    enabled = !isDeleting,
                )
            }
        }
    }
}

@Composable
internal fun DialogPillButton(
    text: String,
    onClick: () -> Unit,
    background: String,
    textColor: String,
    enabled: Boolean = true,
) {
    val fgLayer = if (background.startsWith("linear-gradient")) {
        "$background padding-box, linear-gradient(var(--l-s2, #09142D), var(--l-s2, #09142D)) padding-box"
    } else {
        "linear-gradient($background, $background) padding-box"
    }
    Box(
        modifier = Modifier
            .sHeight(44)
            .sBorderRadius(100)
            .sPadding(13, 28)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .onClick { if (enabled) onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "$fgLayer, ${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("box-sizing", "border-box")
                property("white-space", "nowrap")
                if (!enabled) property("opacity", "0.6")
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(FontWeight.Medium)
                .color(Color(textColor))
                .styleModifier {
                    property("font-family", "Urbanist, sans-serif")
                }
        )
    }
}
